import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { selectIncidentGuid } from '../../../../../slices/commonSelectors';
import {
  KnownHazards,
  HealthAndSafetyInfo,
  SafetyMessageDto,
  SafetyPlanFormFields,
} from '../../../../../types/iapBaseTypes';
import { useCreateSafetyMessage } from '../../../hooks/useCreateSafetyMessage';
import { useUpdateSafetyMessage } from '../../../hooks/useUpdateSafetyMessage';
import { useDeleteSafetyMessage } from '../../../hooks/useDeleteSafetyMessage';
import { addCustomHazard } from '../../../utils/IapUtils';
import SafetyPlanBasicFields from './SafetyPlanBasicFields';
import SafetyPlanHazardsSection from './SafetyPlanHazardsSection';
import SafetyPlanPpeSection from './SafetyPlanPpeSection';
import SafetyPlanHealthSection from './SafetyPlanHealthSection';
import {
  SafetyMessageField,
  SopAndSafeWorkPracticesField,
  CommentsField,
} from './SafetyPlanFormFields';
import {
  getEmptySafetyPlanFormValues,
  getSafetyPlanFormValuesFromDto,
  hasSafetyPlanFormData,
} from './safetyPlanFormUtils';

type SafetyPlanFormCardProps = {
  mode: 'create' | 'edit';
  safetyMessage?: SafetyMessageDto;
  /**
   * Called after successful create, or when user cancels create (hide inline
   * form).
   */
  onDismissCreate?: () => void;
  readOnly?: boolean;
};

const SafetyPlanFormCard = ({
  mode,
  safetyMessage,
  onDismissCreate,
  readOnly = false,
}: SafetyPlanFormCardProps) => {
  const incidentId = useSelector(selectIncidentGuid);
  const createSafetyMessage = useCreateSafetyMessage();
  const updateSafetyMessage = useUpdateSafetyMessage();
  const deleteSafetyMessage = useDeleteSafetyMessage();

  const form = useForm<SafetyPlanFormFields>({
    mode: 'onChange',
    defaultValues:
      mode === 'edit' && safetyMessage
        ? getSafetyPlanFormValuesFromDto(safetyMessage)
        : getEmptySafetyPlanFormValues(),
  });

  const { handleSubmit, reset, control } = form;
  const watchedFields = useWatch({ control }) as
    | SafetyPlanFormFields
    | undefined;
  const hasAnyData = hasSafetyPlanFormData(
    watchedFields ?? getEmptySafetyPlanFormValues()
  );

  // Only re-seed when the edited record changes. Depending on the whole
  // safetyMessage object would discard in-progress edits on every background
  // refetch, since each refetch returns a new reference for the same record.
  useEffect(() => {
    if (mode === 'edit' && safetyMessage) {
      reset(getSafetyPlanFormValuesFromDto(safetyMessage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, safetyMessage?.id, reset]);

  const buildKnownHazardsCreate = (
    formData: SafetyPlanFormFields
  ): KnownHazards => ({
    physicalHazards: formData.physicalHazards,
    environmentalHazards: addCustomHazard(
      formData.environmentalHazards,
      formData.customEnvironmentalHazard
    ),
    otherHazards: addCustomHazard(
      formData.otherHazards,
      formData.customOtherHazard
    ),
    otherHazardsText: formData.otherHazardsText,
  });

  const buildKnownHazardsEdit = (
    formData: SafetyPlanFormFields
  ): KnownHazards => {
    const environmentalHazards = addCustomHazard(
      formData.environmentalHazards,
      formData.customEnvironmentalHazard
    );
    const otherHazards = addCustomHazard(
      formData.otherHazards,
      formData.customOtherHazard
    );
    return {
      physicalHazards:
        formData.physicalHazards.length > 0
          ? formData.physicalHazards
          : undefined,
      environmentalHazards:
        environmentalHazards.length > 0 ? environmentalHazards : undefined,
      otherHazards: otherHazards.length > 0 ? otherHazards : undefined,
      otherHazardsText: formData.otherHazardsText || undefined,
    };
  };

  const buildHealthAndSafetyInfo = (
    formData: SafetyPlanFormFields
  ): HealthAndSafetyInfo | undefined => {
    const healthAndSafetyInfo: HealthAndSafetyInfo = {
      hasPlan: formData.healthAndSafetyPlan,
      location: formData.healthAndSafetyPlanLocation,
    };
    return Object.values(healthAndSafetyInfo).some((v) => v)
      ? healthAndSafetyInfo
      : undefined;
  };

  const onCreateSubmit = async (formData: SafetyPlanFormFields) => {
    const knownHazards = buildKnownHazardsCreate(formData);
    const healthAndSafetyInfo = buildHealthAndSafetyInfo(formData);
    const payload = {
      safetyOfficer: formData.safetyOfficer || undefined,
      dutyPhone: formData.dutyPhone || undefined,
      email: formData.email || undefined,
      datetimeInitiated: formData.datetimeInitiated || undefined,
      knownHazards,
      safetyMessage: formData.safetyMessage || undefined,
      sopAndSafeWorkPractices: formData.sopAndSafeWorkPractices || undefined,
      requiredPpe:
        formData.requiredPpe.length > 0
          ? { items: formData.requiredPpe }
          : undefined,
      comments: formData.comments || undefined,
      healthAndSafetyInfo,
    };
    await createSafetyMessage.mutateAsync(payload);
    onDismissCreate?.();
  };

  const onEditSubmit = async (formData: SafetyPlanFormFields) => {
    if (!safetyMessage) {
      return;
    }
    const knownHazards = buildKnownHazardsEdit(formData);
    const healthAndSafetyInfo = buildHealthAndSafetyInfo(formData);
    // Explicit nulls: the update endpoint preserves omitted (undefined) fields, so an intentionally emptied
    // field must be sent as null to clear the stored value.
    const payload = {
      incidentId: incidentId!,
      safetyOfficer: formData.safetyOfficer || null,
      dutyPhone: formData.dutyPhone || null,
      email: formData.email || null,
      datetimeInitiated: formData.datetimeInitiated || null,
      knownHazards,
      safetyMessage: formData.safetyMessage || null,
      sopAndSafeWorkPractices: formData.sopAndSafeWorkPractices || null,
      requiredPpe:
        formData.requiredPpe.length > 0
          ? { items: formData.requiredPpe }
          : null,
      comments: formData.comments || null,
      healthAndSafetyInfo: healthAndSafetyInfo ?? null,
    };
    await updateSafetyMessage.mutateAsync({
      id: safetyMessage.id,
      data: payload,
    });
  };

  const handleDelete = async () => {
    if (!safetyMessage) {
      return;
    }
    await deleteSafetyMessage.mutateAsync(safetyMessage.id);
  };

  const handleCancelClick = () => {
    if (mode === 'create') {
      reset(getEmptySafetyPlanFormValues());
      onDismissCreate?.();
      return;
    }
    if (safetyMessage) {
      reset(getSafetyPlanFormValuesFromDto(safetyMessage));
    }
  };

  const isCreatePending = createSafetyMessage.isPending;
  const isEditPending =
    updateSafetyMessage.isPending || deleteSafetyMessage.isPending;
  const isPending = mode === 'create' ? isCreatePending : isEditPending;

  return (
    <div className="iap-section-grouping mt-2">
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(
            mode === 'create' ? onCreateSubmit : onEditSubmit
          )}
          className="d-flex flex-column gap-1"
        >
          <fieldset
            disabled={readOnly}
            style={{ border: 'none', padding: 0, margin: 0 }}
          >
            <SafetyPlanBasicFields />
            <SafetyPlanHazardsSection />
            <SafetyMessageField />
            <SopAndSafeWorkPracticesField />
            <SafetyPlanPpeSection />
            <CommentsField />
            <Row>
              <SafetyPlanHealthSection />
            </Row>
          </fieldset>

          {!readOnly && (
            <div className="button-group d-flex justify-content-end border-top border-neutral mt-3 flex-wrap gap-2 pt-2">
              {mode === 'edit' ? (
                <StylishNewButton
                  className="button--error"
                  type="button"
                  onClick={handleDelete}
                  disabled={isPending}
                >
                  Delete
                </StylishNewButton>
              ) : null}
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={handleCancelClick}
                disabled={isPending}
              >
                Cancel
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={!hasAnyData || isPending}
              >
                {mode === 'create'
                  ? isCreatePending
                    ? 'Saving...'
                    : 'Save'
                  : updateSafetyMessage.isPending
                    ? 'Saving...'
                    : 'Save Changes'}
              </StylishNewButton>
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  );
};

export default SafetyPlanFormCard;
